export interface BackendError {
  code?: string;
  message: string;
  parameter?: string;
}

export interface BackendErrorResponse {
  errors?: BackendError[];
  error?: string;
  code?: string;
  message?: string;
  parameter?: string;
}

export class BackendValidationError extends Error {
  public errors?: BackendError[];
  public status?: number;

  constructor(
    message: string,
    public code?: string,
    public parameter?: string,
    errors?: BackendError[],
  ) {
    super(message);
    this.name = 'BackendValidationError';
    this.errors = errors;
  }
}

export const BACKEND_REFUSAL_STATUS = 400;

export const isBackendRefusal = (error: unknown): error is BackendValidationError =>
  error instanceof BackendValidationError && error.status === BACKEND_REFUSAL_STATUS;

export function backendValidationErrorFrom(
  errorData: BackendErrorResponse,
  fallbackMessage: string,
): BackendValidationError {
  if (errorData.errors && errorData.errors.length > 0) {
    const combined = errorData.errors.map(e => e.message).filter(Boolean).join('; ') || 'Validation error';
    return new BackendValidationError(
      combined,
      errorData.errors[0]?.code,
      errorData.errors[0]?.parameter,
      errorData.errors,
    );
  }
  if (errorData.code && errorData.message) {
    return new BackendValidationError(errorData.message, errorData.code, errorData.parameter);
  }
  return new BackendValidationError(errorData.error || fallbackMessage);
}

export function mapParameterToField(
  parameter: string | undefined,
  fieldMap: Record<string, string> = {},
): string | null {
  if (!parameter) return null;
  const matches = Array.from(parameter.matchAll(/\[['"]?(\w+)['"]?\]/g));
  const segments = matches
    .map(match => match[1])
    .filter(segment => !/^\d+$/.test(segment));

  for (let start = 0; start < segments.length; start++) {
    const path = segments.slice(start).join('.');
    if (fieldMap[path]) return fieldMap[path];
  }

  return null;
}

export interface ParsedBackendErrors {
  fieldErrors: Record<string, string>;
  generalError: string | null;
}

export function parseBackendErrors(
  err: unknown,
  fieldMap: Record<string, string> = {},
): ParsedBackendErrors {
  if (!(err instanceof BackendValidationError)) {
    return {
      fieldErrors: {},
      generalError: err instanceof Error ? err.message : 'Unknown error',
    };
  }

  const fieldErrors: Record<string, string> = {};
  const unmapped: string[] = [];

  const errors = err.errors && err.errors.length > 0
    ? err.errors
    : [{ message: err.message, parameter: err.parameter, code: err.code }];

  for (const error of errors) {
    const field = mapParameterToField(error.parameter, fieldMap);
    if (field && !fieldErrors[field]) {
      fieldErrors[field] = error.message;
      continue;
    }
    unmapped.push(error.message);
  }

  return {
    fieldErrors,
    generalError: unmapped.length > 0 ? unmapped.join('; ') : null,
  };
}
